import { useMemo, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSessionContext } from './../useSessionContext';
import { useSecondsRemaining } from './useSecondsRemaining';
import { useLastUserActivity } from './useLastUserActivity';

interface UseSessionExpiry {
  expires: number;
  isExpiring: boolean;
  secondsRemaining: number;
}

const MillisecondsOf = {
  ONE_SECOND: 1000,
  ONE_MINUTE: 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  TEN_MINUTES: 10 * 60 * 1000
};

export const EXPIRING_SECONDS = 60;

export const useSessionExpiry = (
  initialSecondsRemaining: number,
  extend: () => void,
  expire: () => void,
  query: () => void
): UseSessionExpiry => {
  const seconds = useSecondsRemaining(initialSecondsRemaining);
  const { disableExtendSessionOnFocus } = useSessionContext();

  const isExpiring = useMemo<boolean>(
    () => seconds > -1 && seconds <= EXPIRING_SECONDS,
    [seconds]
  );

  const expires = useRef<number>(0);

  useEffect(() => {
    if (initialSecondsRemaining === -1) {
      expires.current = 0;
    } else {
      expires.current = Date.now() + seconds * MillisecondsOf.ONE_SECOND;
    }
  }, [seconds, initialSecondsRemaining]);

  const lastActivity = useLastUserActivity();

  const lastExtension = useRef(0);
  const extendOnWindowFocus = useDebouncedCallback(() => {
    if (!disableExtendSessionOnFocus) {
      lastExtension.current = Date.now();
      if (isExpiring) {
        query();
      } else {
        extend();
      }
    }
  }, 500);

  useEffect(() => {
    window.addEventListener('focus', extendOnWindowFocus as EventListener);

    return () => {
      window.removeEventListener('focus', extendOnWindowFocus as EventListener);
    };
  }, [
    extend,
    query,
    isExpiring,
    disableExtendSessionOnFocus,
    extendOnWindowFocus
  ]);

  useEffect(() => {
    const distance = lastActivity - lastExtension.current;
    if (
      lastActivity < expires.current &&
      distance > MillisecondsOf.ONE_MINUTE &&
      !isExpiring
    ) {
      extend();
      lastExtension.current = lastActivity;
    }
  }, [lastActivity, isExpiring, extend]);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = expires.current - Date.now();
      const since = Date.now() - lastExtension.current;

      if (
        remaining < MillisecondsOf.TEN_MINUTES &&
        since > MillisecondsOf.FIVE_MINUTES &&
        !isExpiring
      ) {
        lastExtension.current = Date.now();
        query();
      }
    }, MillisecondsOf.ONE_SECOND);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expires.current !== 0 && seconds === 0 && expire) {
      expire();
    }
  }, [expires, seconds, expire]);

  return {
    expires: expires.current,
    isExpiring,
    secondsRemaining: seconds
  };
};
